import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import { CheckCircle2, FileSpreadsheet, Upload } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, uploadFile } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Alert, Badge, Card, PageHeader } from '../../components/ui/display';
import { Checkbox, Field, Input, Select } from '../../components/ui/inputs';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { formatDate, formatMoney } from '../../utils/format';
import { useAuth } from '../auth/AuthProvider';
import { sources } from '../masters/sources';
import { EMPTY_MAPPING, FIELDS, guessMapping, type Mapping, mapRows } from './mapping';

// ── Types ────────────────────────────────────────────────────────────

interface Sheet {
  name: string;
  headerRow: number;
  rows: string[][];
}
type Action = 'create' | 'overwrite' | 'skip' | 'none' | 'error';
interface Preview {
  app: { id: string; name: string };
  advertisingAccount: { id: string; accountName: string; currencyCode: string } | null;
  revenueAccount: { id: string; accountName: string; currencyCode: string } | null;
  defaultGstRate: string;
  assignmentChanges: {
    kind: 'advertising' | 'revenue';
    accountName: string;
    type: 'create' | 'extend';
    from: string;
    previousFrom?: string;
  }[];
  rows: {
    line: number;
    date: string;
    marketing: {
      action: Action;
      spend?: string;
      gst?: string;
      total?: string;
      gstFromSheet?: boolean;
      message?: string;
    };
    revenue: { action: Action; amount?: string; rate?: string; inr?: string; message?: string };
  }[];
  summary: {
    rows: number;
    errors: number;
    marketing: { create: number; overwrite: number; skip: number; spend: string; gst: string };
    revenue: { create: number; overwrite: number; skip: number; amount: string; inr: string };
  };
}

const ACTION_UI: Record<
  Action,
  { label: string; tone: 'green' | 'blue' | 'slate' | 'rose' | 'amber' }
> = {
  create: { label: 'New', tone: 'blue' },
  overwrite: { label: 'Overwrite', tone: 'amber' },
  skip: { label: 'Exists – skip', tone: 'slate' },
  none: { label: '—', tone: 'slate' },
  error: { label: 'Error', tone: 'rose' },
};

function errorText(err: unknown) {
  if (!(err instanceof ApiError)) return 'Request failed';
  const first = err.errors[0];
  return first && first.message !== err.message ? `${err.message} — ${first.message}` : err.message;
}

// ── Page ─────────────────────────────────────────────────────────────

/** Import past daily marketing spend and revenue from a Google Sheet / Excel export. */
const IMPORT_INTRO =
  'Bring daily ads spend and revenue from a Google Sheet or Excel file into the app. In Google Sheets use File → Download → Microsoft Excel (.xlsx) or CSV.';

export function ImportPage({ embedded = false }: { embedded?: boolean }) {
  const { can } = useAuth();
  const isAdmin = can('fx.override');
  const fileInput = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [sheets, setSheets] = useState<Sheet[]>([]);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [headerRow, setHeaderRow] = useState(0);
  const [mapping, setMapping] = useState<Mapping>(EMPTY_MAPPING);
  const [dateFormat, setDateFormat] = useState<'DMY' | 'MDY'>('DMY');
  const [appId, setAppId] = useState('');
  const [adAccountId, setAdAccountId] = useState('');
  const [revenueAccountId, setRevenueAccountId] = useState('');
  const [gstMode, setGstMode] = useState<'SHEET' | 'CALCULATE'>('SHEET');
  const [rateMode, setRateMode] = useState<'SHEET' | 'MONTHLY'>(isAdmin ? 'SHEET' : 'MONTHLY');
  const [duplicates, setDuplicates] = useState<'SKIP' | 'OVERWRITE'>('SKIP');
  const [extendAssignments, setExtendAssignments] = useState(true);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [done, setDone] = useState<{ created: number; updated: number; skipped: number } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const sheet = sheets[sheetIndex];
  const headers = sheet?.rows[headerRow] ?? [];

  const autoMap = (hs: string[]) => setMapping(guessMapping(hs));

  const parse = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.set('file', file);
      return uploadFile<{ sheets: Sheet[] }>('/imports/parse', form);
    },
    onSuccess: (r) => {
      setSheets(r.sheets);
      setSheetIndex(0);
      setHeaderRow(r.sheets[0]?.headerRow ?? 0);
      autoMap(r.sheets[0]?.rows[r.sheets[0].headerRow] ?? []);
      setPreview(null);
      setDone(null);
      setError(null);
    },
    onError: (err) => setError(errorText(err)),
  });

  /** Rows mapped from the sheet; lines without a readable date (totals, notes) are ignored. */
  const mapped = useMemo(
    () =>
      sheet
        ? mapRows(sheet.rows, headerRow, mapping, dateFormat)
        : { rows: [], ignored: 0, invalid: [] as string[] },
    [sheet, headerRow, mapping, dateFormat],
  );

  const request = () => ({
    appId,
    advertisingAccountId: mapping.spend !== '' ? adAccountId : '',
    revenueAccountId: mapping.revenue !== '' ? revenueAccountId : '',
    rows: mapped.rows,
    options: { gstMode, rateMode, duplicates, extendAssignments },
  });
  const ready =
    mapped.rows.length > 0 &&
    mapped.invalid.length === 0 &&
    !!appId &&
    ((mapping.spend !== '' && !!adAccountId) || (mapping.revenue !== '' && !!revenueAccountId));

  const runPreview = useMutation({
    mutationFn: () => api.post<Preview>('/imports/daily/preview', request()),
    onSuccess: (p) => {
      setPreview(p);
      setError(null);
    },
    onError: (err) => setError(errorText(err)),
  });
  const commit = useMutation({
    mutationFn: () =>
      api.post<{ created: number; updated: number; skipped: number }>(
        '/imports/daily/commit',
        request(),
      ),
    onSuccess: (r) => {
      setDone(r);
      setPreview(null);
      setError(null);
    },
    onError: (err) => setError(errorText(err)),
  });
  const invalidatePreview = () => setPreview(null);

  return (
    <>
      {embedded ? (
        <p className="mb-4 text-sm text-slate-500">{IMPORT_INTRO}</p>
      ) : (
        <PageHeader title="Import past data" description={IMPORT_INTRO} />
      )}
      {error && (
        <Alert tone="error" title="Import problem">
          {error}
        </Alert>
      )}
      {done && (
        <Alert tone="success" title="Import finished">
          {done.created} entries created, {done.updated} updated, {done.skipped} skipped. Check them
          in{' '}
          <Link to="/marketing" className="underline">
            Marketing
          </Link>{' '}
          and{' '}
          <Link to="/revenue" className="underline">
            Revenue
          </Link>
          ; the import is recorded in the audit log.
        </Alert>
      )}

      <div className="space-y-6">
        {/* 1. File */}
        <Card
          title="1. Choose the file"
          description="One app per import. CSV or .xlsx, up to 5 MB."
        >
          <input
            ref={fileInput}
            type="file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            aria-label="Spreadsheet file"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) {
                setFileName(f.name);
                parse.mutate(f);
              }
              e.target.value = '';
            }}
          />
          <div className="flex flex-wrap items-center gap-3">
            <Button
              icon={<Upload className="size-4" />}
              loading={parse.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {sheets.length ? 'Choose another file' : 'Choose file'}
            </Button>
            {fileName && (
              <span className="flex items-center gap-1.5 text-sm text-slate-600">
                <FileSpreadsheet className="size-4 text-emerald-600" /> {fileName}
              </span>
            )}
            {sheets.length > 1 && (
              <Select
                aria-label="Sheet"
                value={sheetIndex}
                className="w-56"
                onChange={(e) => {
                  const i = Number(e.target.value);
                  setSheetIndex(i);
                  setHeaderRow(sheets[i]!.headerRow);
                  autoMap(sheets[i]!.rows[sheets[i]!.headerRow] ?? []);
                  invalidatePreview();
                }}
              >
                {sheets.map((s, i) => (
                  <option key={s.name} value={i}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </div>
        </Card>

        {sheet && (
          <>
            {/* 2. Columns */}
            <Card
              title="2. Match the columns"
              description="Columns are matched automatically from the header; check them. Other columns (profit, totals…) are ignored."
            >
              <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Field
                  label="Header row"
                  htmlFor="imp-header"
                  help="Row number that has the column names"
                >
                  <Input
                    id="imp-header"
                    type="number"
                    min={1}
                    max={Math.min(30, sheet.rows.length)}
                    value={headerRow + 1}
                    onChange={(e) => {
                      const v = Math.max(0, Number(e.target.value) - 1);
                      setHeaderRow(v);
                      autoMap(sheet.rows[v] ?? []);
                      invalidatePreview();
                    }}
                  />
                </Field>
                <Field
                  label="Date format"
                  htmlFor="imp-datefmt"
                  help="How dates like 01/09/2026 are written"
                >
                  <Select
                    id="imp-datefmt"
                    value={dateFormat}
                    onChange={(e) => {
                      setDateFormat(e.target.value as 'DMY' | 'MDY');
                      invalidatePreview();
                    }}
                  >
                    <option value="DMY">Day/Month/Year (01/09/2026 = 1 Sep)</option>
                    <option value="MDY">Month/Day/Year (09/01/2026 = 1 Sep)</option>
                  </Select>
                </Field>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                {FIELDS.map((f) => (
                  <Field
                    key={f.key}
                    label={f.label}
                    htmlFor={`imp-${f.key}`}
                    help={f.help}
                    required={f.key === 'date'}
                  >
                    <Select
                      id={`imp-${f.key}`}
                      value={mapping[f.key]}
                      onChange={(e) => {
                        setMapping((m) => ({ ...m, [f.key]: e.target.value }));
                        invalidatePreview();
                      }}
                    >
                      <option value="">— Not in the file —</option>
                      {headers.map((h, i) => (
                        <option key={i} value={i}>
                          {h || `Column ${i + 1}`}
                        </option>
                      ))}
                    </Select>
                  </Field>
                ))}
              </div>
              <p className="mt-4 text-sm text-slate-600">
                {mapped.rows.length} day(s) found
                {mapped.ignored > 0 &&
                  `, ${mapped.ignored} line(s) without a date ignored (totals, notes)`}
                .
              </p>
              {mapped.invalid.length > 0 && (
                <ul className="mt-2 list-disc pl-5 text-sm text-rose-600">
                  {mapped.invalid.slice(0, 8).map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                  {mapped.invalid.length > 8 && <li>…and {mapped.invalid.length - 8} more</li>}
                </ul>
              )}
            </Card>

            {/* 3. Target and options */}
            <Card title="3. Where the data goes">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <Field label="App" htmlFor="imp-app" required>
                  <SearchableSelect
                    inputId="imp-app"
                    value={appId}
                    remote={sources.app()}
                    onChange={(v) => {
                      setAppId(v);
                      invalidatePreview();
                    }}
                  />
                </Field>
                <Field
                  label="Ads account (spend)"
                  htmlFor="imp-ad"
                  help={mapping.spend === '' ? 'No spend column mapped' : undefined}
                >
                  <SearchableSelect
                    inputId="imp-ad"
                    clearable
                    disabled={mapping.spend === ''}
                    value={adAccountId}
                    remote={sources.advertisingAccount()}
                    onChange={(v) => {
                      setAdAccountId(v);
                      invalidatePreview();
                    }}
                  />
                </Field>
                <Field
                  label="Revenue account"
                  htmlFor="imp-rev"
                  help={mapping.revenue === '' ? 'No revenue column mapped' : undefined}
                >
                  <SearchableSelect
                    inputId="imp-rev"
                    clearable
                    disabled={mapping.revenue === ''}
                    value={revenueAccountId}
                    remote={sources.revenueAccount()}
                    onChange={(v) => {
                      setRevenueAccountId(v);
                      invalidatePreview();
                    }}
                  />
                </Field>
              </div>
              <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
                <Field label="GST on spend" htmlFor="imp-gst">
                  <Select
                    id="imp-gst"
                    value={gstMode}
                    onChange={(e) => {
                      setGstMode(e.target.value as 'SHEET' | 'CALCULATE');
                      invalidatePreview();
                    }}
                  >
                    <option value="SHEET" disabled={mapping.gst === ''}>
                      Keep the GST from the sheet
                    </option>
                    <option value="CALCULATE">Calculate at the default rate</option>
                  </Select>
                </Field>
                <Field
                  label="Exchange rate for revenue"
                  htmlFor="imp-rate"
                  help={isAdmin ? undefined : 'Sheet rates can be imported by an administrator'}
                >
                  <Select
                    id="imp-rate"
                    value={rateMode}
                    onChange={(e) => {
                      setRateMode(e.target.value as 'SHEET' | 'MONTHLY');
                      invalidatePreview();
                    }}
                  >
                    <option value="SHEET" disabled={!isAdmin || mapping.revenueInr === ''}>
                      From the sheet (INR ÷ revenue per day)
                    </option>
                    <option value="MONTHLY">Monthly rates (Currency Rates)</option>
                  </Select>
                </Field>
                <Field label="Days already entered" htmlFor="imp-dup">
                  <Select
                    id="imp-dup"
                    value={duplicates}
                    onChange={(e) => {
                      setDuplicates(e.target.value as 'SKIP' | 'OVERWRITE');
                      invalidatePreview();
                    }}
                  >
                    <option value="SKIP">Keep what is saved (skip)</option>
                    <option value="OVERWRITE">Replace with the sheet</option>
                  </Select>
                </Field>
              </div>
              <Checkbox
                className="mt-4"
                label={`If the accounts were not yet assigned to the app on those dates, start the assignment on the first imported date${can('masters.write') ? '' : ' (administrator only)'}`}
                checked={extendAssignments}
                disabled={!can('masters.write')}
                onChange={(e) => {
                  setExtendAssignments(e.target.checked);
                  invalidatePreview();
                }}
              />
              <div className="mt-5">
                <Button
                  disabled={!ready}
                  loading={runPreview.isPending}
                  onClick={() => runPreview.mutate()}
                >
                  Preview import
                </Button>
              </div>
            </Card>
          </>
        )}

        {/* 4. Preview */}
        {preview && (
          <Card
            title="4. Check and import"
            description={`${preview.app.name}${preview.advertisingAccount ? ` · ${preview.advertisingAccount.accountName}` : ''}${preview.revenueAccount ? ` · ${preview.revenueAccount.accountName} (${preview.revenueAccount.currencyCode})` : ''}`}
            bodyClassName="p-0"
            actions={
              <Button
                icon={<CheckCircle2 className="size-4" />}
                disabled={preview.summary.errors > 0}
                loading={commit.isPending}
                onClick={() => commit.mutate()}
              >
                Import{' '}
                {preview.summary.marketing.create +
                  preview.summary.marketing.overwrite +
                  preview.summary.revenue.create +
                  preview.summary.revenue.overwrite}{' '}
                entries
              </Button>
            }
          >
            <div className="grid grid-cols-2 gap-3 border-b border-slate-100 p-4 lg:grid-cols-4">
              <Stat
                label="Spend to save"
                value={formatMoney(preview.summary.marketing.spend)}
                sub={`${preview.summary.marketing.create} new · ${preview.summary.marketing.overwrite} replace · ${preview.summary.marketing.skip} skip`}
              />
              <Stat label="GST" value={formatMoney(preview.summary.marketing.gst)} />
              <Stat
                label="Revenue to save"
                value={formatMoney(
                  preview.summary.revenue.amount,
                  preview.revenueAccount?.currencyCode ?? 'INR',
                )}
                sub={`${preview.summary.revenue.create} new · ${preview.summary.revenue.overwrite} replace · ${preview.summary.revenue.skip} skip`}
              />
              <Stat label="Revenue in INR" value={formatMoney(preview.summary.revenue.inr)} />
            </div>
            {preview.assignmentChanges.length > 0 && (
              <div className="border-b border-amber-100 bg-amber-50 px-4 py-2 text-sm text-amber-800">
                {preview.assignmentChanges.map((c) => (
                  <p key={c.kind}>
                    {c.type === 'create'
                      ? `${c.accountName} will be assigned to ${preview.app.name} from ${formatDate(c.from)}.`
                      : `${c.accountName}'s assignment to ${preview.app.name} will start on ${formatDate(c.from)} instead of ${formatDate(c.previousFrom)}.`}
                  </p>
                ))}
              </div>
            )}
            {preview.summary.errors > 0 && (
              <div className="border-b border-rose-100 bg-rose-50 px-4 py-2 text-sm text-rose-700">
                {preview.summary.errors} row(s) have errors. Nothing is imported until they are
                fixed in the file (or the options changed).
              </div>
            )}
            <div className="relative max-h-[520px] overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Line</th>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-right">Spend</th>
                    <th className="px-3 py-2 text-right text-violet-700">GST</th>
                    <th className="px-3 py-2 text-left">Spend</th>
                    <th className="px-3 py-2 text-right">Revenue</th>
                    <th className="px-3 py-2 text-right">Rate</th>
                    <th className="px-3 py-2 text-right">INR</th>
                    <th className="px-3 py-2 text-left">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.rows.map((r) => {
                    const err = r.marketing.action === 'error' || r.revenue.action === 'error';
                    return (
                      <tr key={r.line} className={clsx(err && 'bg-rose-50/60')}>
                        <td className="px-3 py-1.5 text-slate-400">{r.line}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap">{formatDate(r.date)}</td>
                        <td className="num px-3 py-1.5 text-right">
                          {r.marketing.spend ? formatMoney(r.marketing.spend) : ''}
                        </td>
                        <td className="num px-3 py-1.5 text-right text-violet-700">
                          {r.marketing.gst ? formatMoney(r.marketing.gst) : ''}
                          {r.marketing.gstFromSheet && (
                            <span title="GST from the sheet (differs from the calculated amount)">
                              {' '}
                              *
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-1.5">
                          <Status a={r.marketing.action} message={r.marketing.message} />
                        </td>
                        <td className="num px-3 py-1.5 text-right">
                          {r.revenue.amount
                            ? formatMoney(
                                r.revenue.amount,
                                preview.revenueAccount?.currencyCode ?? 'INR',
                              )
                            : ''}
                        </td>
                        <td className="num px-3 py-1.5 text-right text-xs">
                          {r.revenue.rate && preview.revenueAccount?.currencyCode !== 'INR'
                            ? `₹${r.revenue.rate}`
                            : ''}
                        </td>
                        <td className="num px-3 py-1.5 text-right">
                          {r.revenue.inr ? formatMoney(r.revenue.inr) : ''}
                        </td>
                        <td className="px-3 py-1.5">
                          <Status a={r.revenue.action} message={r.revenue.message} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="px-4 py-2 text-xs text-slate-500">
              * GST kept from the sheet where it differs from {preview.defaultGstRate}% (it is saved
              as invoice GST). Imported rows are marked as imported; sheet exchange rates are saved
              as manual rates and are not changed by later rate recalculations.
            </p>
          </Card>
        )}
      </div>
    </>
  );
}

function Status({ a, message }: { a: Action; message?: string }) {
  if (a === 'none') return <span className="text-slate-300">—</span>;
  return (
    <span className="flex flex-col gap-0.5">
      <Badge tone={ACTION_UI[a].tone}>{ACTION_UI[a].label}</Badge>
      {message && a === 'error' && <span className="text-xs text-rose-600">{message}</span>}
    </span>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="num text-base font-semibold text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-400">{sub}</p>}
    </div>
  );
}
